import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmDialog, HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmInput } from '@spartan-ng/helm/input';
import { registrarCheckInRetroativo } from 'src/app/api/generated/v3/check-in/check-in';
import type { CheckinRowVm, JanelaVm } from './checkin-row.vm';
import {
  horaDeParedeParaInstante,
  instanteParaHoraDeParede,
  mensagemErroRetroativo,
} from './checkin-retroativo';

/**
 * "Check-in retroativo": registra na janela o horário em que a pessoa DE FATO
 * entrou, quando o portão ficou sem rede ou o operador não conseguiu bipar na
 * hora (GUE-430 / ADR-0021).
 *
 * O que ele NÃO é: um check-in comum com data escolhida. Só aparece em janela
 * que o produtor habilitou, só aceita horário dentro de `[inicio, fim]` daquela
 * janela e nunca no futuro — as três regras valem também no servidor, aqui elas
 * existem para o operador não gastar uma ida ao servidor para descobrir.
 *
 * Erro fica INLINE no próprio diálogo, não num `Swal` por cima: um segundo
 * diálogo sobre este não recebe clique.
 *
 * ```html
 * <app-checkin-retroativo #retroativo (concluido)="list.refresh()" />
 * ```
 */
@Component({
  selector: 'app-checkin-retroativo',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, HlmInput, ...HlmDialogImports],
  template: `
    <hlm-dialog #dialog [disableClose]="enviando()">
      <hlm-dialog-content
        *hlmDialogPortal
        [showCloseButton]="!enviando()"
        class="admin-portal-scope flex w-[min(94vw,26rem)] flex-col gap-0 overflow-hidden rounded-card bg-card p-0 text-card-foreground shadow-float"
      >
        <div hlmDialogHeader class="border-b border-border px-4 py-4">
          <h2 hlmDialogTitle class="font-display text-base font-bold text-foreground">
            Check-in retroativo
          </h2>
          <p hlmDialogDescription class="m-0 text-sm text-muted-foreground">
            Informe o horário em que a entrada aconteceu de verdade.
          </p>
        </div>

        <div class="flex flex-col gap-3 p-4">
          @if (row(); as r) {
            <dl class="m-0 flex flex-col gap-1">
              <div class="flex items-baseline justify-between gap-3">
                <dt class="shrink-0 text-[0.8rem] text-muted-foreground">Participante</dt>
                <dd data-testid="retroativo-participante" class="m-0 text-right text-[0.8rem] break-words">
                  {{ r.nome }}
                </dd>
              </div>
              <div class="flex items-baseline justify-between gap-3">
                <dt class="shrink-0 text-[0.8rem] text-muted-foreground">Janela</dt>
                <dd data-testid="retroativo-janela" class="m-0 text-right text-[0.8rem] break-words">
                  {{ janela()?.periodo }}
                </dd>
              </div>
            </dl>
          }

          <label class="flex flex-col gap-1 text-[0.8rem] font-semibold text-foreground">
            Horário do check-in
            <!-- Data COMPLETA e não só hora: janela de virada de ano-novo
                 começa dia 31 e termina dia 1º, e um campo de hora sozinho não
                 tem como dizer qual dos dois dias. -->
            <input
              hlmInput
              type="datetime-local"
              data-testid="retroativo-input"
              [attr.min]="min()"
              [attr.max]="max()"
              [value]="valor()"
              [disabled]="enviando()"
              (input)="aoDigitar($event)"
            />
          </label>
          <p class="m-0 text-[0.7rem] text-muted-foreground">
            Fuso do evento: {{ timezone() }}.
          </p>

          @if (erro()) {
            <p
              role="alert"
              data-testid="retroativo-erro"
              class="m-0 rounded-input border border-destructive/40 bg-destructive/10 px-3 py-2 text-[0.8rem] text-destructive"
            >
              {{ erro() }}
            </p>
          }
        </div>

        <div hlmDialogFooter class="flex flex-wrap justify-end gap-2 border-t border-border px-4 py-3">
          <button type="button" hlmBtn variant="ghost" size="sm" [disabled]="enviando()" (click)="fechar()">
            Cancelar
          </button>
          <button
            type="button"
            hlmBtn
            variant="solid"
            size="sm"
            data-testid="retroativo-confirmar"
            [disabled]="enviando()"
            (click)="confirmar()"
          >
            {{ enviando() ? 'Registrando...' : 'Registrar check-in' }}
          </button>
        </div>
      </hlm-dialog-content>
    </hlm-dialog>
  `,
})
export class CheckinRetroativoComponent {
  private readonly http = inject(HttpClient);

  // Decorator e não `viewChild()`: o harness compila em JIT, e lá a query por
  // signal nunca resolve (NG0951) — mesma nota do painel de detalhes.
  @ViewChild(HlmDialog) private dialog!: HlmDialog;

  /**
   * A lista da tela ficou desatualizada. Sai no sucesso E no 409 — nos dois o
   * ingresso tem um check-in que a tela ainda não mostra.
   */
  readonly concluido = output<void>();

  readonly row = signal<CheckinRowVm | null>(null);
  readonly janela = signal<JanelaVm | null>(null);
  readonly valor = signal('');
  readonly enviando = signal(false);
  readonly erro = signal('');

  private eventoId = '';

  /** Fuso do EVENTO — nunca o do dispositivo, nunca offset chumbado (ADR-0009). */
  readonly timezone = computed(() => this.row()?.timezone ?? 'America/Sao_Paulo');
  readonly min = computed(() => instanteParaHoraDeParede(this.janela()?.inicio, this.timezone()));
  readonly max = computed(() => instanteParaHoraDeParede(this.janela()?.fim, this.timezone()));

  /**
   * Abre já preenchido com o INÍCIO da janela: é o palpite mais barato de
   * corrigir (o operador só empurra a hora para frente) e garante um valor
   * dentro do intervalo mesmo em navegador que ignora `min`/`max`.
   *
   * Recebe tudo por argumento, sem `input()`: em JIT o `setInput` é no-op
   * silencioso, e um modal que abre com os campos vazios no teste não prova
   * nada.
   */
  abrir(eventoId: string, row: CheckinRowVm, janela: JanelaVm): void {
    this.eventoId = eventoId;
    this.row.set(row);
    this.janela.set(janela);
    this.erro.set('');
    this.enviando.set(false);
    this.valor.set(instanteParaHoraDeParede(janela.inicio, row.timezone));
    this.dialog.open();
  }

  fechar(): void {
    this.dialog.close();
  }

  protected aoDigitar(evento: Event): void {
    this.valor.set((evento.target as HTMLInputElement).value);
    this.erro.set('');
  }

  confirmar(): void {
    const row = this.row();
    const janela = this.janela();
    if (!row || !janela?.id || this.enviando()) return;

    const valor = this.valor();
    if (!valor) {
      this.erro.set('Informe o horário do check-in.');
      return;
    }
    // Comparação de string funciona porque o formato é `YYYY-MM-DDTHH:mm`
    // (ordenável). É o mesmo par min/max do input — repetido aqui porque
    // `min`/`max` de `datetime-local` não bloqueiam valor digitado em todo
    // navegador, e o 400 do servidor é uma ida a mais para dizer o óbvio.
    const min = this.min();
    const max = this.max();
    if ((min && valor < min) || (max && valor > max)) {
      this.erro.set(`Escolha um horário dentro da janela (${janela.periodo}).`);
      return;
    }

    const dataCheckin = horaDeParedeParaInstante(valor, this.timezone());
    if (!dataCheckin) {
      this.erro.set('Horário inválido.');
      return;
    }
    if (new Date(dataCheckin).getTime() > Date.now()) {
      this.erro.set('O check-in retroativo não pode estar no futuro.');
      return;
    }

    this.enviando.set(true);
    registrarCheckInRetroativo(this.http, this.eventoId, row.ingressoId, janela.id, { dataCheckin })
      .then(() => {
        this.enviando.set(false);
        this.fechar();
        this.concluido.emit();
      })
      .catch((err: unknown) => {
        this.enviando.set(false);
        this.erro.set(mensagemErroRetroativo(err));
        // 409: o check-in existe e a tela mostra o contrário. Fica aberto com a
        // mensagem, mas a lista atrás precisa parar de mentir.
        if ((err as { status?: unknown } | null)?.status === 409) this.concluido.emit();
      });
  }
}
